import type { AssetClassTag } from "./class-mix";

export interface Currency {
  id: number | string;
  iso_4217_currency_code?: string | null;
  iso4217Currency_code?: string | null;
  latest_to_sek_rate?: string | number | null;
  latest_to_sek_rate_at?: string | null;
}

export const MonetaryBasis = { REAL: "REAL", NOMINAL: "NOMINAL" } as const;
export type MonetaryBasisValue = (typeof MonetaryBasis)[keyof typeof MonetaryBasis];

export interface OptimizeRequest {
  monetary_basis: MonetaryBasisValue;
  include_deposit_fee: boolean;
  include_advisory_fee: boolean;
  include_product_fees: boolean;
  include_financial_plan: boolean;
  reserved_amount: string;
  deposit_amount: string;
  withdrawal_amount: string;
}

export interface ProspectItem {
  isin: string | null;
  asset_name: string | null;
  asset_id?: number | null;
  weight: number;
  value: number;
  fee: number | null;
  financial_asset_category: string | null;
  asset_class_name?: string | null;
  is_liquidity?: boolean;
  classes?: AssetClassTag[];
}

export interface ProspectPortfolio {
  id: number;
  mean_return: number;
  volatility: number;
  cvar: number | null;
  variance?: number | null;
  total_fee: number | null;
  total_value: number | null;
  items: ProspectItem[];
}

export interface OptimizeResponse {
  id: string | number | null;
  prospect_portfolios: ProspectPortfolio[];
  start_value: number | null;
  computedAt?: string | null;
}

export interface QuantTransaction {
  asset_id: number | null;
  asset_name: string | null;
  isin: string | null;
  transaction_type: number | string | null;
  quantity: number | null;
  amount: number | null;
  amount_sek: number | null;
  sell_all: boolean;
}

export interface TransactionSetResponse {
  id: number;
  starting_portfolio_id: number | null;
  transactions: QuantTransaction[];
  target_prospect_portfolio_id: number;
}

export interface PortfolioHoldingSnapshot {
  isin: string | null;
  assetName: string | null;
  assetId?: number | null;
  value: number | null;
  weight: number | null;
  fee: number | null;
  classes?: AssetClassTag[];
}

export interface CurrentPortfolio {
  holdings: PortfolioHoldingSnapshot[];
  volatility: number | null;
  meanReturn: number | null;
  cvar: number | null;
  value: number | null;
}

export type QuantBffStep =
  | "auth"
  | "optimize"
  | "last-optimize"
  | "analysis"
  | "transactions"
  | "portfolio";

export class QuantBffError extends Error {
  readonly step: QuantBffStep;
  readonly status?: number;
  constructor(step: QuantBffStep, message: string, status?: number) {
    super(message);
    this.name = "QuantBffError";
    this.step = step;
    this.status = status;
  }
}
